<template>
  <ul class="tabs-container">
    <li v-for="tab in displayedTabs" class='tab' :class="{current: isCurrentTab(tab.index)}" :key="`tab-${tab.index}`" @click="setCurrentTab(tab)">
      <button type="button" v-tooltip="tab.name.length > 19 ? tab.name : ''">
        <!--          If there is no name use the index for the name, truncate longer names-->
        {{(tab.name || `Tab ${tab.index+1}`).replace(/(.{19})..+/, "$1&hellip;") }}
      </button>
    </li>

    <li class="dropdown tab" v-if="displayShowMoreTab">
      <button class="tab-toggle" type="button" id="moreTabsDropDown" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
        More
      </button>
      <div class="dropdown-menu dropdown-menu-right" aria-labelledby="moreTabsDropDown">
        <button v-for="tab in additionalTabs" class="dropdown-item" @click="setCurrentTabFromAdditionalTabs(tab)" type="button">
          {{tab.name || `Tab ${tab.index+1}`}}
        </button>
      </div>
    </li>
  </ul>
</template>
<script lang="ts">
  import Vue from "vue";
  import selectedMixin from "../mixins/selectedMixin";
  import {Area} from "../enums/area";
  import {DetailViewContentElementType} from "~/enums/detailViews/detailViewContentElementType";
  import ObjectModelConditionalRuleEvaluator from "~/models/objectModels/objectModelConditionalRuleEvaluator";
  import { RouterHelper } from '~/helpers/routerHelper'

  export default Vue.extend({
    mixins: [selectedMixin],
    components: {},
    props: {
      isInEditMode: {
        type: Boolean,
        default: false
      },
    },
    data: function () {
      return {
        Area: Area
      };
    },
    mounted() {
      //Set the tab from url if there is a tab param
      let tabFromURL = this.$router.currentRoute.query.tab;
      if (tabFromURL) {
        this.selectedDetailView.setCurrentTabFormURL(tabFromURL)
      }
    },
    methods: {
      setCurrentTab(tab: {name: string, index: number}): void {
        Vue.set(this.selectedDetailView, "current_tab_index",  tab.index);
        //Use either index or tab name in the url
        const tabName = (tab.name || `${tab.index+ 1}` ).replace(/" "/g, "").toLowerCase();
        RouterHelper.appendQuery(this.$router, { tab: tabName }, { mode: 'push' })
      },
      setCurrentTabFromAdditionalTabs(tab: {name: string, index: number}): void {
        this.setCurrentTab(tab)
      },
      isCurrentTab(index: number): boolean {
        return this.selectedDetailView.current_tab_index === index;
      },
      isTabHidden(nameOrIndex: string | number): boolean {
        if (this.isInEditMode) {
          let tabIndex: number;
          if (typeof nameOrIndex === 'string') {
            tabIndex = this.selectedDetailView.tabs.indexOf(nameOrIndex);
          } else {
            tabIndex = nameOrIndex;
          }
          // there is no element for the first tab, so it's always visible
          if (tabIndex === 0) {
            return false;
          }
          const tabBreakEl = this.selectedDetailView.tabBreaks[tabIndex - 1];
          const visible = ObjectModelConditionalRuleEvaluator.evaluate(this.selectedObjectModelEntry, tabBreakEl, 'visible');
          return !visible;
        } else {
          const hidden = this.selectedObjectModelEntry.view_data?.hidden_tabs ?? [];
          return hidden.includes(nameOrIndex);
        }
      }
    },
    computed: {
      tabs(): string[] {
        // prevent duplicate tab breaks from appearing in case of data corruption
        return this.selectedDetailView.tabs.slice(0, this.selectedDetailView.tabBreaks.length + 1)
      },
      displayedTabs(): Array<{name: string, index: number}> {
        const current: string = this.tabs[this.selectedDetailView.current_tab_index];
        let tabs = this.tabs.map((t, i) => ({name: t, index: i})).filter(({name, index}) => !this.isTabHidden(name) && !this.isTabHidden(index));

        if (this.selectedDetailView.current_tab_index >= 5 && !this.isTabHidden(current)) {
          tabs = tabs.slice(0, 4);
          tabs.push({index: this.selectedDetailView.current_tab_index, name: current});
        } else {
          tabs = tabs.slice(0, 5);
        }
        return tabs;
      },
      additionalTabs(): Array<{name: string, index: number}> {
        const current = this.tabs[this.selectedDetailView.current_tab_index];
        let tabs = this.tabs.map((t, i) => ({name: t, index: i})).filter(({name, index}) => !this.isTabHidden(name) && !this.isTabHidden(index));

        if (this.selectedDetailView.current_tab_index >= 5 && !this.isTabHidden(current)) {
          tabs = tabs.slice(4).filter(({name, index}) => index !== this.selectedDetailView.current_tab_index);
        } else {
          tabs = tabs.slice(5);
        }

        return tabs;
      },
      displayShowMoreTab(): boolean {
        const tabs = this.tabs.filter((name, index) => !this.isTabHidden(name) && !this.isTabHidden(index));
        return tabs.length > 5;
      },
    },
  });

</script>